/* LEGEND — HeatStrip: rotation radar. A slim band between the topbar and the
   workspace showing the ladder-service's 1 Hz cross-sectional heat board +
   hot-set rotation alerts, so which symbol the tape is rotating INTO is visible
   without watching every chart. Chips follow the USER'S WATCHLIST ORDER, not
   heat rank — rank-sorted chips reshuffled every second and read as churn; with
   fixed positions, heat is carried by intensity/dot and rank lives in the
   tooltip. PERF: this component owns its own `heat:*` subscriptions and state —
   the 1 Hz board re-renders ONLY this strip, never Terminal's tree (per-message
   full-tree renders froze the whole UI once already). */
import React from 'react';
import { fmtMoneyShort, etClock } from '../lib/format';
import { subscribeHeatLeaderboard, subscribeHeatRotation } from '../data/source';
import { playRotation, unlockAudio } from '../lib/sound';

const { useState, useRef, useEffect } = React;
const h = React.createElement;

const STALE_MS = 5000;    // 1 Hz feed quiet this long → "heat feed offline"
const FLASH_MS = 5200;    // rotation pulse window (covers the 6-beat CSS animation)
const MUTE_KEY = 'legend-heat-mute';

// "12:03:44" in ET — now literally the terminal's ET clock, not a second copy of it.
const rotClock = etClock;

// Hover receipt for one chip/row: heat rank, flow rate, print rate,
// buy-aggressor share, rvol (0 = baseline unseeded → em-dash, never a fake
// number), spread warning. A symbol with no NBBO yet has a meaningless
// spreadOk — "spread —", not a scare. Rank lives HERE because chips/rows keep
// the user's watchlist order (position no longer encodes rank).
export function heatReceipt(s: any): string {
  const parts = [
    '#' + s.rank,
    fmtMoneyShort(s.dvps) + '/s',
    (Number(s.tradesPs) || 0).toFixed(1) + ' prints/s',
    Math.round((Number(s.tapRatio) || 0) * 100) + '% buy-side',
    (s.rvol ? Number(s.rvol).toFixed(1) + '×' : '—') + ' rvol',
  ];
  if (s.hasQuote === false) parts.push('spread —');
  else if (s.spreadOk === false) parts.push('⚠ spread wide');
  return s.symbol + ' · ' + parts.join(' · ');
}

// Traffic-light band for a heat value: red = cold tape, amber = warming,
// green = hot. Thirds, not a continuous blend — glanceability over precision.
// Shared by the strip chips AND the watchlist row meters so the two surfaces
// can never disagree about what a given heat level looks like.
export function heatBandColor(hv: number): string {
  return hv >= 0.66 ? 'var(--up)' : hv >= 0.33 ? 'var(--warn)' : 'var(--down)';
}

function HeatChip({ s, flashing, onSelect }: any) {
  const heat = Math.max(0, Math.min(1, Number(s.heat) || 0));
  const hc = heatBandColor(heat);
  // Chips show the SAME reading as the watchlist rows: the 0–1 heat score in
  // its band color + trend arrow. No price % here — the chip's ~60s tape move
  // next to the row's day-change column read as two contradicting numbers
  // (tooltip still carries the receipt). Background tints with the band.
  return h('button', {
    className: 'hs-chip' + (s.hot ? ' hot' : '') + (flashing ? ' flash' : ''),
    style: { '--chip-bg': `color-mix(in srgb, ${hc} ${Math.round(heat * 26)}%, transparent)` } as any,
    title: heatReceipt(s),
    onClick: () => onSelect(s),
  },
    s.hot ? h('span', { className: 'hs-dot' }) : null,
    h('span', { className: 'hs-sym' }, s.symbol),
    h('span', { className: 'hs-heat num', style: { color: hc } }, heat.toFixed(2)),
    s.trend === 'up' ? h('span', { className: 'hs-trend up' }, '▲')
      : s.trend === 'down' ? h('span', { className: 'hs-trend down' }, '▼') : null);
}

export function HeatStrip({ onSelect, soundOn, order }: { onSelect: (s: any) => void; soundOn: boolean; order: string[] }) {
  const [board, setBoard] = useState<any>(null);
  const [stale, setStale] = useState(false);
  // symbol → rotation event; drives the chip pulse and the temporary chip for a
  // rotator outside the top-6 slice. Cleared per-symbol after the flash window.
  const [flashes, setFlashes] = useState<Record<string, any>>({});
  const [lastRot, setLastRot] = useState<any>(null);
  const [muted, setMuted] = useState<boolean>(() => {
    try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
  });
  const mutedRef = useRef(muted); mutedRef.current = muted;
  // Master terminal sound gates the strip additively: master off silences heat
  // too; the strip's own mute additionally silences only heat. Ref-mirrored so
  // the subscription closure never goes stale.
  const soundOnRef = useRef(soundOn); soundOnRef.current = soundOn;
  const lastMsgRef = useRef(0);
  const lastRotKeyRef = useRef('');
  const flashTimersRef = useRef<Record<string, any>>({});

  useEffect(() => {
    const timers = flashTimersRef.current;
    const offBoard = subscribeHeatLeaderboard((b: any) => {
      lastMsgRef.current = Date.now();
      setBoard(b);
    });
    const offRot = subscribeHeatRotation((ev: any) => {
      // A Redis pub/sub reconnect upstream can stack a duplicate listener and
      // re-deliver the same event — one rotation must never double-beep.
      const key = ev.symbol + '@' + ev.ts;
      if (key === lastRotKeyRef.current) return;
      lastRotKeyRef.current = key;
      setFlashes((prev) => ({ ...prev, [ev.symbol]: ev }));
      setLastRot(ev);
      // AudioContext is lazy-built + gesture-unlocked in lib/sound (Terminal
      // installs a first-gesture unlock), so this can auto-play by rotation time.
      if (soundOnRef.current && !mutedRef.current) playRotation();
      if (timers[ev.symbol]) clearTimeout(timers[ev.symbol]);
      timers[ev.symbol] = setTimeout(() => {
        setFlashes((prev) => { const n = { ...prev }; delete n[ev.symbol]; return n; });
        delete timers[ev.symbol];
      }, FLASH_MS);
    });
    // Staleness watchdog: only flips state on a transition, so the 1s clock
    // costs no renders while the feed is healthy (or before it ever spoke).
    const watch = setInterval(() => {
      if (!lastMsgRef.current) return;
      const s = Date.now() - lastMsgRef.current >= STALE_MS;
      setStale((prev) => (prev === s ? prev : s));
    }, 1000);
    return () => {
      offBoard(); offRot(); clearInterval(watch);
      Object.values(timers).forEach((t) => clearTimeout(t as any));
      flashTimersRef.current = {};
    };
  }, []);

  function toggleMute() {
    unlockAudio();
    setMuted((v) => {
      const nv = !v;
      try { localStorage.setItem(MUTE_KEY, nv ? '1' : '0'); } catch { /* ignore */ }
      if (!nv && soundOnRef.current) playRotation(); // audible confirmation, same as the scanner toggle
      return nv;
    });
  }

  // Chips = the user's watchlist symbols that are on the current board, in the
  // user's row order. Fixed positions: a chip brightens/dims in place instead
  // of jumping around at 1 Hz. Symbols below the activity floors simply have no
  // chip (the watchlist rows still show them).
  const bySym = new Map<string, any>(board ? (board.symbols || []).map((s: any) => [s.symbol, s]) : []);
  const listed: any[] = (order || []).filter((sym) => bySym.has(sym)).map((sym) => bySym.get(sym));
  const listedSet = new Set(listed.map((s: any) => s.symbol));
  // A rotation outside the listed set still needs a visible pulse — synthesize
  // a temporary chip from the event payload. The event carries no trend/rvol,
  // and it just entered the hot set, so hot is true by construction. Spread
  // state passes through from the event (never fabricated — an asserted-clean
  // spread on a wide book would mislead the entry decision).
  const extras = Object.keys(flashes).filter((sym) => !listedSet.has(sym)).map((sym) => {
    const ev = flashes[sym];
    return { symbol: sym, rank: ev.rank, heat: ev.heat, movePct: ev.movePct, trend: 'flat', hot: true, price: ev.price, dvps: ev.dvps, tradesPs: ev.tradesPs, tapRatio: ev.tapRatio, rvol: 0, hasQuote: ev.hasQuote, spreadOk: ev.spreadOk };
  });
  const chips = listed.concat(extras);

  return h('div', { className: 'heat-strip' + (stale ? ' stale' : '') },
    h('span', { className: 'hs-label mono', title: board ? board.active + ' symbols past the activity floors' : undefined }, 'HEAT'),
    board
      ? (chips.length
          ? h('div', { className: 'hs-chips' },
              chips.map((s: any) => {
                const fl = flashes[s.symbol];
                // Key on the rotation ts while flashing: React reuses DOM nodes for
                // stable keys, so a re-rotation inside the window remounts the chip
                // and restarts the one-shot CSS pulse — no JS animation timers.
                return h(HeatChip, { key: fl ? s.symbol + '@' + fl.ts : s.symbol, s, flashing: !!fl, onSelect });
              }))
          : h('span', { className: 'hs-quiet mono' }, 'no active tape on your list'))
      // Quiet placeholder until the first board lands (also what MOCK shows).
      : h('span', { className: 'hs-quiet mono' }, 'rotation radar — waiting for feed'),
    stale ? h('span', { className: 'hs-offline mono' }, 'heat feed offline') : null,
    lastRot ? h('button', {
      className: 'hs-last mono', title: 'Chart ' + lastRot.symbol,
      onClick: () => onSelect(lastRot),
    }, 'last: ' + lastRot.symbol + ' ' + rotClock(lastRot.ts)) : null,
    h('button', {
      className: 'hs-mute' + (muted ? ' off' : ''),
      title: muted ? 'Unmute rotation alerts' : 'Mute rotation alerts',
      onClick: toggleMute,
    }, muted ? '🔕' : '🔔'));
}
